import { useState } from 'react';
import { shootCoins, shootProgress, studioNextLevel, studioLevel } from '@/core/domain/studio';
import type { GameSave } from '@/app/services';
import { useBack } from '@/ui/back/backStack';
import { SpotArt } from '@/ui/art/SpotArt';
import { Icon } from '@/ui/components/Icon';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useSaveDerived, useServices } from '@/ui/services/ServicesContext';
import { criterionText } from '@/ui/studio/criteria';
import shell from './CatalogSheet.module.css';
import board from './OrdersSheet.module.css';
import { NpcFace } from './OrdersSheet';
import styles from './StudioSheet.module.css';

type Request = GameSave['studio']['requests'][number];

/**
 * Sổ hẹn Studio (studio-nano.md §4): the neighbours' bookings with their brief and a live checklist of
 * what the studio already has. No deadlines; "Để sau" never costs anything.
 */
export function StudioSheet({
  onClose,
  onStart,
}: {
  onClose: () => void;
  /** A booking was put on the set: the shell closes the sheet and walks to the studio. */
  onStart: () => void;
}) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const requests = useSave((s) => s.studio.requests);
  const session = useSave((s) => s.studio.session);
  const level = useSaveDerived((s) => studioLevel(content, s));
  const next = useSaveDerived((s) => studioNextLevel(content, s));
  // The checklist reads the studio as it is right now (one key per booking, so no new arrays per render).
  const progress = useSaveDerived((s) =>
    s.studio.requests
      .map((r) => {
        const def = content.studioShoots.get(r.shootId);
        return def
          ? shootProgress(content, s, def)
              .map((p) => (p.met ? '1' : '0'))
              .join('')
          : '';
      })
      .join('|'),
  );
  const [confirmSkip, setConfirmSkip] = useState<Request | null>(null);
  useBack(() => {
    if (confirmSkip) setConfirmSkip(null);
    else onClose();
  });

  const npcName = (npcId: string) => t(content.npcs.get(npcId)?.nameKey ?? '');
  const initial = (npcId: string) => npcName(npcId).split(' ').pop()?.slice(0, 1) ?? '';
  const met = progress.split('|');

  return (
    <div
      className={shell.scrim}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className={`${shell.sheet} ${board.board}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('ui.studio.title')}
        data-testid="studio-sheet"
      >
        <div className={shell.grip} aria-hidden="true" />
        <header className={shell.header}>
          <h2 className={shell.title}>{t('ui.studio.title')}</h2>
          <span className={board.slots}>
            {t('ui.tasks.progress', {
              current: requests.length,
              target: content.balance.studio.maxOpenRequests,
            })}
          </span>
          <button
            type="button"
            className={shell.iconBtn}
            aria-label={t('ui.catalog.close')}
            onClick={onClose}
          >
            <Icon name="close" size={26} />
          </button>
        </header>

        <div className={styles.fame} data-testid="studio-fame">
          <Icon name="star" size={24} />
          <span className={styles.fameLevel}>
            {level === 0 ? t('ui.studio.level_none') : t('ui.studio.level', { level })}
          </span>
          {next ? (
            <>
              <span
                className={styles.meter}
                role="meter"
                aria-label={t('ui.hud.studio_label')}
                aria-valuemin={next.prev}
                aria-valuemax={next.need}
                aria-valuenow={next.points}
              >
                <span
                  className={styles.fill}
                  style={{
                    width: `${String(Math.round(((next.points - next.prev) / (next.need - next.prev)) * 100))}%`,
                  }}
                />
              </span>
              <span className={styles.fameText}>
                {t('ui.studio.level_progress', { points: next.points, need: next.need, next: next.level })}
              </span>
            </>
          ) : (
            <span className={styles.fameText}>{t('ui.studio.level_max')}</span>
          )}
        </div>

        {confirmSkip ? (
          <div className={board.confirm} role="alertdialog" aria-label={t('ui.studio.skip')}>
            <p>{t('ui.studio.skip_confirm', { npc: npcName(confirmSkip.npcId) })}</p>
            <div className={board.actions}>
              <button
                type="button"
                className={board.secondary}
                onClick={() => {
                  setConfirmSkip(null);
                }}
              >
                {t('ui.studio.keep')}
              </button>
              <button
                type="button"
                className={board.primary}
                onClick={() => {
                  dispatch({ type: 'skipShootRequest', requestId: confirmSkip.requestId });
                  setConfirmSkip(null);
                }}
              >
                {t('ui.studio.let_go')}
              </button>
            </div>
          </div>
        ) : (
          <ul className={board.list} data-testid="studio-requests">
            {requests.length === 0 && (
              <li className={board.empty}>
                <SpotArt name="empty_orders" fallback="camera" size={130} />
                <p>{t('ui.studio.empty')}</p>
              </li>
            )}
            {requests.map((r, i) => {
              const def = content.studioShoots.get(r.shootId);
              if (!def) return null;
              const onSet = session?.requestId === r.requestId;
              const ticks = met[i] ?? '';
              return (
                <li key={r.requestId} className={board.card} data-ready={onSet || undefined}>
                  <div className={board.who}>
                    <NpcFace npcId={r.npcId} initial={initial(r.npcId)} />
                    <span className={board.name}>{npcName(r.npcId)}</span>
                    {def.model && def.model !== r.npcId && (
                      <span className={styles.model}>
                        {t('ui.studio.model', { name: npcName(def.model) })}
                      </span>
                    )}
                  </div>
                  <p className={board.quote}>“{t(def.briefKey)}”</p>
                  <ul className={board.lines}>
                    {def.criteria.map((c, k) => {
                      const ok = ticks[k] === '1';
                      return (
                        <li key={k} data-ok={ok || undefined}>
                          <span>{criterionText(c, t, content)}</span>
                          <span className={board.qty}>
                            {ok ? (
                              <Icon name="check" size={18} className={board.tick} />
                            ) : (
                              t('ui.studio.not_yet')
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <p className={board.reward}>
                    {t('ui.studio.reward', {
                      coins: shootCoins(content, def, 'excellent'),
                      rep: content.balance.studio.reputation.excellent,
                    })}
                  </p>
                  <div className={board.actions}>
                    <button
                      type="button"
                      className={board.secondary}
                      disabled={onSet}
                      onClick={() => {
                        setConfirmSkip(r);
                      }}
                    >
                      {t('ui.studio.skip')}
                    </button>
                    {onSet ? (
                      <button type="button" className={board.primary} onClick={onStart}>
                        {t('ui.studio.back_to_set')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={board.primary}
                        data-testid={`studio-start-${r.requestId}`}
                        disabled={session !== null}
                        onClick={() => {
                          if (dispatch({ type: 'startShoot', requestId: r.requestId }).ok) onStart();
                        }}
                      >
                        {t('ui.studio.start')}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!confirmSkip && session !== null && session.phase === 'setup' && (
          <p className={board.footer}>{t('ui.studio.busy', { npc: npcName(session.npcId) })}</p>
        )}
        {!confirmSkip && session === null && requests.length > 0 && (
          <p className={board.footer}>{t('ui.studio.footer')}</p>
        )}
      </section>
    </div>
  );
}
